/***
 *
 *   FormView
 *   View table for existing forms, or launch form builder.
 *   Also surfaces the typed template catalog (consent / intake /
 *   risk assessment / EDD starters) for installing account copies.
 *
 **********/

import React, { useState, useEffect, useContext } from "react";
import Axios from "axios";
import {
  useAPI,
  Table,
  Card,
  BlankSlateMessage,
  TitleRow,
  Button,
  ViewContext,
  AccountNav,
} from "components/lib";

import { SCORING_FORM_TYPES } from "./scoringTemplate";

const TYPE_LABELS = {
  consent: "Consent",
  intake: "Intake",
  risk_assessment: "Risk assessment",
  edd: "EDD",
  client_onboarding_v2: "Client onboarding v2",
};

function formMisc(form) {
  return typeof form.misc === "string"
    ? safeParse(form.misc)
    : form.misc || {};
}

function safeParse(value) {
  try {
    return JSON.parse(value) || {};
  } catch (err) {
    return {};
  }
}

export function FormView(props) {
  const formsAPI = useAPI("/api/form");
  const templatesAPI = useAPI("/api/form/templates");
  const [forms, setForms] = useState([]);
  const [installing, setInstalling] = useState(null);
  const viewContext = useContext(ViewContext);

  const createForm = () => {
    viewContext.modal.show(
      {
        title: "Create Form",
        form: {
          name: {
            label: "Form Name",
            type: "text",
            required: true,
          },
        },
        buttonText: "Create",
        url: "/api/form",
        method: "POST",
      },
      (form, res) => {
        window.location.href = `/account/form/${res.id}`;
      }
    );
  };

  // Rename an existing form in place. The name lives on the form record, so a
  // PATCH with just { name } reuses the standard update path (misc/scoring are
  // left untouched). The modal callback receives the form state; forwarding it to
  // the Table's edit callback refreshes the row's name cell without a reload. The
  // success notification is raised by the shared Form component (res.data.message).
  const renameForm = (form, callback) => {
    viewContext.modal.show(
      {
        title: "Rename Form",
        form: {
          name: {
            label: "Form Name",
            type: "text",
            required: true,
            value: form.name,
            errorMessage: "Please enter a form name",
          },
        },
        buttonText: "Save",
        url: `/api/form/${form.id}`,
        method: "PATCH",
      },
      (res) => callback(res)
    );
  };

  const installTemplate = async (template) => {
    try {
      setInstalling(template.id);
      await Axios.post(`/api/form/templates/${template.id}/install`);
      viewContext.notification.show(
        `${template.name} installed`,
        "success",
        true,
        "toast",
        "check"
      );
      window.location.reload();
    } catch (err) {
      setInstalling(null);
      viewContext.handleError(err);
    }
  };

  useEffect(() => {
    if (formsAPI?.data?.length > 0) {
      setForms(
        formsAPI.data.map((form) => {
          const misc = formMisc(form);
          const type = misc.type || null;
          return {
            name: form.name,
            type: type ? TYPE_LABELS[type] || type : "",
            scoring: SCORING_FORM_TYPES.includes(type)
              ? misc.scoring_mode === "calculated"
                ? "Calculated"
                : "Manual"
              : "",
            id: form.id,
          };
        })
      );
    }
  }, [formsAPI.data]);

  const templates = templatesAPI?.data || [];

  return (
    <>
      <AccountNav />

      {templates.length > 0 && (
        <Card>
          <TitleRow title="Template Catalog" />
          <table className="w-full">
            <tbody>
              {templates.map((template) => (
                <tr key={template.id} className="border-b border-slate-100">
                  <td className="py-2">{template.name}</td>
                  <td className="py-2">
                    <span className="inline-block px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">
                      {TYPE_LABELS[template.type] || template.type}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    <Button
                      small
                      text={installing === template.id ? "Installing…" : "Install"}
                      action={() => installTemplate(template)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {forms.length === 0 ? (
        <BlankSlateMessage
          title="Form Builder"
          text="Create forms to collect information from your users, or install a template from the catalog above."
          buttonText="Create Form"
          action={createForm}
        />
      ) : (
        <Card>
          <TitleRow title="Manage Forms">
            <Button small text="Create" action={createForm} />
          </TitleRow>
          <Table
            data={forms}
            show={["name", "type", "scoring"]}
            hide={["id"]}
            loading={formsAPI.loading}
            actions={{
              edit: renameForm,
              view: { url: "/account/form", col: "id" },
            }}
          />
        </Card>
      )}
    </>
  );
}
